import { useMemo, useState } from 'react';
import { useCreateFaq, useDeleteFaq, useFaqList, useUpdateFaq } from '@/hooks/useFaq';
import { Faq } from '@/types';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { BulkSelectionToolbar } from '@/components/list/BulkSelectionToolbar';
import { useBulkSelection } from '@/hooks/useBulkSelection';
import { Edit, Plus, Trash2 } from 'lucide-react';

const emptyForm: Partial<Faq> = {
  pertanyaanId: '',
  jawabanId: '',
  urutan: 1,
  isActive: true,
};

export function FaqList() {
  const [page, setPage] = useState(1);
  const [open, setOpen] = useState(false);
  const [pendingDeleteIds, setPendingDeleteIds] = useState<number[]>([]);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [form, setForm] = useState<Partial<Faq>>(emptyForm);

  const { data, isLoading } = useFaqList({ page, limit: 10 });
  const createFaq = useCreateFaq();
  const updateFaq = useUpdateFaq();
  const deleteFaq = useDeleteFaq();
  const selection = useBulkSelection((data?.data || []).map((item) => item.id));

  const totalPage = useMemo(() => Math.max(1, Math.ceil((data?.total || 0) / 10)), [data?.total]);

  const openCreate = () => {
    setEditingId(null);
    setForm(emptyForm);
    setOpen(true);
  };

  const openEdit = (item: Faq) => {
    setEditingId(item.id);
    setForm({
      pertanyaanId: item.pertanyaanId || '',
      jawabanId: item.jawabanId || '',
      urutan: item.urutan || 1,
      isActive: item.isActive,
    });
    setOpen(true);
  };

  const submitForm = async () => {
    if (!form.pertanyaanId?.trim()) return;
    if (editingId) {
      await updateFaq.mutateAsync({ id: editingId, data: form });
    } else {
      await createFaq.mutateAsync(form);
    }
    setOpen(false);
  };

  const submitDelete = async () => {
    if (!pendingDeleteIds.length) return;
    for (const id of pendingDeleteIds) {
      await deleteFaq.mutateAsync(id);
    }
    selection.clearSelection();
    setPendingDeleteIds([]);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-bold tracking-tight">FAQ</h2>
          <p className="text-muted-foreground">Kelola pertanyaan yang sering diajukan.</p>
        </div>
        <Button onClick={openCreate}>
          <Plus className="mr-2 h-4 w-4" />
          Tambah FAQ
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Daftar FAQ</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <BulkSelectionToolbar
            className="m-4 mb-0"
            selectedCount={selection.selectedCount}
            itemLabel="FAQ"
            onClear={selection.clearSelection}
            onDelete={() => setPendingDeleteIds(selection.selectedIds)}
            deleteDisabled={deleteFaq.isPending}
          />
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-12">
                  <Checkbox checked={selection.allSelected} onCheckedChange={(checked) => selection.toggleAll(Boolean(checked))} />
                </TableHead>
                <TableHead>Pertanyaan</TableHead>
                <TableHead>Urutan</TableHead>
                <TableHead>Aktif</TableHead>
                <TableHead className="w-28">Aksi</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow><TableCell colSpan={5} className="py-8 text-center">Memuat...</TableCell></TableRow>
              ) : data?.data.length ? (
                data.data.map((item) => (
                  <TableRow key={item.id} className={selection.isSelected(item.id) ? 'bg-muted/30' : undefined}>
                    <TableCell>
                      <Checkbox checked={selection.isSelected(item.id)} onCheckedChange={(checked) => selection.toggleOne(item.id, Boolean(checked))} />
                    </TableCell>
                    <TableCell className="font-medium">{item.pertanyaanId || '-'}</TableCell>
                    <TableCell>{item.urutan || '-'}</TableCell>
                    <TableCell>{item.isActive ? 'Ya' : 'Tidak'}</TableCell>
                    <TableCell className="space-x-2">
                      <Button variant="outline" size="icon" onClick={() => openEdit(item)}>
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button variant="destructive" size="icon" onClick={() => setPendingDeleteIds([item.id])}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow><TableCell colSpan={5} className="py-8 text-center">Belum ada FAQ.</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <div className="flex items-center justify-end gap-2">
        <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>Sebelumnya</Button>
        <span className="text-sm text-muted-foreground">Halaman {page} dari {totalPage}</span>
        <Button variant="outline" size="sm" disabled={page >= totalPage} onClick={() => setPage((p) => p + 1)}>Selanjutnya</Button>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingId ? 'Ubah FAQ' : 'Buat FAQ'}</DialogTitle>
            <DialogDescription>Isi pertanyaan dan jawaban.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <Input
              placeholder="Urutan"
              type="number"
              value={form.urutan ?? 1}
              onChange={(e) => setForm((v) => ({ ...v, urutan: Number(e.target.value) }))}
            />
            <Textarea
              placeholder="Pertanyaan (Indonesia)"
              value={form.pertanyaanId || ''}
              onChange={(e) => setForm((v) => ({ ...v, pertanyaanId: e.target.value }))}
            />
            <Textarea
              placeholder="Jawaban (Indonesia)"
              value={form.jawabanId || ''}
              onChange={(e) => setForm((v) => ({ ...v, jawabanId: e.target.value }))}
            />
            <div className="flex items-center gap-2">
              <Checkbox
                id="faq-active"
                checked={!!form.isActive}
                onCheckedChange={(checked) => setForm((v) => ({ ...v, isActive: !!checked }))}
              />
              <label htmlFor="faq-active" className="text-sm">Aktif</label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Batal</Button>
            <Button onClick={submitForm}>Simpan</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={pendingDeleteIds.length > 0} onOpenChange={() => setPendingDeleteIds([])}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Hapus FAQ</DialogTitle>
            <DialogDescription>Tindakan ini tidak dapat dibatalkan.</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPendingDeleteIds([])}>Batal</Button>
            <Button variant="destructive" onClick={submitDelete}>Hapus</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
